import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, User, Lock, Phone, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = "/api";

const AuthModal = ({ isOpen, onClose, mode = "login" }) => {
  const { initiateGoogleLogin } = useAuth();
  const { t } = useLanguage();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  // Sync isLogin state when mode prop changes
  useEffect(() => {
    if (isOpen) {
      setIsLogin(mode === "login");
      setShowEmailForm(false);
      setFormData({ name: "", email: "", phone: "", password: "" });
    }
  }, [isOpen, mode]);

  const handleGoogleSignIn = () => {
    initiateGoogleLogin("customer");
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Login
      if (!formData.email || !formData.password) {
        toast.error("Please enter email and password");
        return;
      }
    } else {
      // Signup
      if (!formData.name || !formData.email || !formData.phone || !formData.address?.trim() || !formData.password) {
        toast.error("Please fill in all fields, including your address");
        return;
      }
      if (formData.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (isLogin) {
        await axios.post(`${API}/auth/email-login`, {
          email: formData.email,
          password: formData.password,
        }, { withCredentials: true });
        toast.success("Login successful!");
      } else {
        await axios.post(`${API}/auth/email-signup`, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address.trim(),
          password: formData.password,
        }, { withCredentials: true });
        toast.success("Account created successfully!");
      }
      // Reload to update auth state
      window.location.href = "/portal";
    } catch (error) {
      console.error("Auth failed:", error);
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (isLogin ? "Login failed. Please try again." : "Failed to create account. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setShowEmailForm(false);
    setFormData({ name: "", email: "", phone: "", password: "" });
    setIsLogin(mode === "login");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-md" aria-describedby="auth-modal-description">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase text-center">
            {isLogin ? t("auth.customerLogin", "Customer Login") : t("auth.createAccount", "Create Account")}
          </DialogTitle>
          <p id="auth-modal-description" className="sr-only">
            {t("auth.modalDesc", "Sign in or create an account to access your customer portal")}
          </p>
        </DialogHeader>

        <div className="min-h-[300px]">
          {!showEmailForm ? (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#39FF14]" />
                </div>
                <p className="text-[#A1A1AA]">
                  {isLogin 
                    ? t("auth.signInDesc", "Sign in to view your appointments and manage your account")
                    : t("auth.signUpDesc", "Create an account to book services and manage appointments")
                  }
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {/* Google Sign In */}
                <Button
                  data-testid="auth-google-btn"
                  onClick={handleGoogleSignIn}
                  className="w-full bg-white text-black hover:bg-gray-100 font-semibold py-6 rounded-full flex items-center justify-center gap-3"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t("auth.continueWithGoogle", "Continue with Google")}
                </Button>

                {/* Apple Sign In */}
                <Button
                  data-testid="auth-apple-btn"
                  onClick={handleGoogleSignIn}
                  className="w-full bg-black text-white border border-white/20 hover:bg-gray-900 font-semibold py-6 rounded-full flex items-center justify-center gap-3"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {t("auth.continueWithApple", "Continue with Apple")}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#27272A]"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#0A0A0A] text-[#A1A1AA]">{t("common.or", "or")}</span>
                  </div>
                </div>

                {/* Email Sign In/Up */}
                <Button
                  data-testid="auth-email-btn"
                  onClick={() => setShowEmailForm(true)}
                  variant="outline"
                  className="w-full border-[#27272A] text-white hover:bg-[#27272A] font-semibold py-6 rounded-full flex items-center justify-center gap-3"
                >
                  <Mail className="w-5 h-5" />
                  {isLogin ? t("auth.signInWithEmail", "Sign in with Email") : t("auth.signUpWithEmail", "Sign up with Email")}
                </Button>
              </div>

              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
                <p className="text-[#A1A1AA] text-sm text-center">
                  {t("auth.worksWithAnyEmail", "Works with any email: Gmail, iCloud, Yahoo, Outlook, and more")}
                </p>
              </div>

              <div className="text-center mt-4">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-[#39FF14] text-sm hover:underline"
                >
                  {isLogin ? t("auth.noAccount", "Don't have an account? Sign up") : t("auth.haveAccount", "Already have an account? Sign in")}
                </button>
              </div>
            </div>
          ) : (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-2">
                  {isLogin ? t("auth.signIn", "Sign In") : t("auth.createAccount", "Create Your Account")}
                </h3>
                <p className="text-[#A1A1AA]">
                  {isLogin ? t("auth.enterCredentials", "Enter your credentials") : t("auth.enterDetails", "Enter your details to get started")}
                </p>
              </div>

              <form onSubmit={handleEmailAuth} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label className="text-[#A1A1AA]">{t("auth.fullName", "Full Name")}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        data-testid="auth-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                        placeholder="John Doe"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-[#A1A1AA]">{t("auth.email", "Email Address")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                    <Input
                      data-testid="auth-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                {!isLogin && (
                  <div>
                    <Label className="text-[#A1A1AA]">{t("staff.phone", "Phone Number")}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        data-testid="auth-phone-input"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                        placeholder="+81 90-1234-5678"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                )}

                {!isLogin && (
                  <div>
                    <Label className="text-[#A1A1AA]">Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        data-testid="auth-address-input"
                        value={formData.address || ""}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                        placeholder="e.g. 3725 Camp Foster"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-[#A1A1AA]">{t("auth.password", "Password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                    <Input
                      data-testid="auth-password-input"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
                      placeholder={isLogin ? t("auth.yourPassword", "Your password") : t("auth.minChars", "Min. 6 characters")}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="auth-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold py-6 rounded-full"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    isLogin ? t("auth.signIn", "Sign In") : t("auth.createAccount", "Create Account")
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setShowEmailForm(false)}
                  className="w-full text-[#A1A1AA] text-sm hover:text-white transition-colors"
                >
                  ← {t("common.back", "Back to sign in options")}
                </button>
              </form>
            </div>
          )}
        </div>

        <p className="text-center text-[#A1A1AA] text-xs mt-2">
          {t("auth.terms", "By signing in, you agree to our terms of service")}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
